// Schedules a refresh of a short-lived pre-signed URL shortly BEFORE it expires.
//
// The backend returns `expiresIn` (seconds) with every signed URL. Callers store
// `expiresAt = requestTime + expiresIn * 1000` and hand us `getExpiresAt`; we fire
// `refresh()` a little ahead of that moment so playback / downloads never hit a 403.

/** How long before expiry we try to fetch a replacement URL. */
export const SIGNED_URL_REFRESH_SKEW_MS = 90 * 1000;

const MIN_DELAY_MS = 5 * 1000; // never spin faster than this
const RETRY_DELAY_MS = 15 * 1000;
const MAX_RETRIES = 3;

/**
 * @param {object} opts
 * @param {() => number|null} opts.getExpiresAt  epoch ms when the current URL dies (null = unknown)
 * @param {() => number|null} [opts.getTtlMs]    total lifetime of the current URL, used to shrink the skew
 *                                               for very short URLs (skew is capped at 25% of the ttl)
 * @param {() => Promise<boolean|void>} opts.refresh
 *        Fetch + apply a new URL. Return `false` to go idle (nothing left to keep fresh);
 *        throw to retry (up to MAX_RETRIES).
 * @param {number} [opts.skewMs]
 * @returns {{ start: () => void, restart: (o?: { immediate?: boolean }) => void, cancel: () => void }}
 */
// TODO: add a check to see if the URL is expired and if it is, refresh it
export function createSignedUrlRefresher({
  getExpiresAt,
  getTtlMs,
  refresh,
  skewMs = SIGNED_URL_REFRESH_SKEW_MS,
}) {
  let timer = null;
  let cancelled = false;
  let active = false;
  let running = false;
  let retries = 0;
  let listening = false;

  const effectiveSkew = () => {
    const ttl = getTtlMs?.();
    return ttl && ttl > 0 ? Math.min(skewMs, ttl * 0.25) : skewMs;
  };

  const clear = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const isDue = () => {
    const expiresAt = getExpiresAt();
    return !!expiresAt && expiresAt - effectiveSkew() <= Date.now();
  };

  const arm = (delayOverride) => {
    clear();
    if (cancelled || !active) return;
    const expiresAt = getExpiresAt();
    if (!expiresAt) {
      active = false;
      return;
    }
    const delay =
      delayOverride ?? Math.max(MIN_DELAY_MS, expiresAt - effectiveSkew() - Date.now());
    timer = setTimeout(run, delay);
  };

  async function run() {
    timer = null;
    if (cancelled || !active || running) return;
    running = true;
    let keepGoing = true;
    try {
      keepGoing = (await refresh()) !== false;
      retries = 0;
    } catch {
      running = false;
      retries += 1;
      if (retries > MAX_RETRIES) {
        active = false;
        return;
      }
      arm(RETRY_DELAY_MS);
      return;
    }
    running = false;
    if (keepGoing) arm();
    else active = false;
  }

  // Background tabs throttle timers; catch up as soon as the tab is visible again.
  const onVisibility = () => {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (cancelled || !active || running) return;
    if (isDue()) {
      clear();
      run();
    }
  };

  const listen = () => {
    if (listening || typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', onVisibility);
    listening = true;
  };

  const unlisten = () => {
    if (!listening || typeof document === 'undefined') return;
    document.removeEventListener('visibilitychange', onVisibility);
    listening = false;
  };

  return {
    start() {
      if (cancelled) return;
      active = true;
      retries = 0;
      listen();
      arm();
    },
    restart({ immediate = false } = {}) {
      if (cancelled) return;
      active = true;
      retries = 0;
      listen();
      if (immediate && isDue() && !running) {
        clear();
        run();
      } else if (!running) {
        arm();
      }
    },
    cancel() {
      cancelled = true;
      active = false;
      clear();
      unlisten();
    },
  };
}
